// Мелкая матричная алгебра для оружия (column-major 4×4, как WebGL).
// Система мира: +X вперёд (yaw=0), +Y влево, +Z вверх. Модели оружия
// собраны в той же системе (Blender-оси), glTF-экспорт перевёл их в Y-up —
// GLTF_TO_WORLD возвращает обратно (поворот +90° вокруг X).

export type M4 = Float32Array;

export function m4(): M4 {
  const m = new Float32Array(16);
  m[0] = m[5] = m[10] = m[15] = 1;
  return m;
}

export function m4Identity(o: M4): M4 {
  o.fill(0);
  o[0] = o[5] = o[10] = o[15] = 1;
  return o;
}

export function m4Mul(o: M4, a: Float32Array, b: Float32Array): M4 {
  const r = _tmp;
  for (let c = 0; c < 4; c++) {
    const b0 = b[c * 4]!, b1 = b[c * 4 + 1]!, b2 = b[c * 4 + 2]!, b3 = b[c * 4 + 3]!;
    r[c * 4] = a[0]! * b0 + a[4]! * b1 + a[8]! * b2 + a[12]! * b3;
    r[c * 4 + 1] = a[1]! * b0 + a[5]! * b1 + a[9]! * b2 + a[13]! * b3;
    r[c * 4 + 2] = a[2]! * b0 + a[6]! * b1 + a[10]! * b2 + a[14]! * b3;
    r[c * 4 + 3] = a[3]! * b0 + a[7]! * b1 + a[11]! * b2 + a[15]! * b3;
  }
  o.set(r);
  return o;
}
const _tmp = new Float32Array(16);

export function m4Translate(o: M4, x: number, y: number, z: number): M4 {
  m4Identity(o);
  o[12] = x; o[13] = y; o[14] = z;
  return o;
}

export function m4Scale(o: M4, s: number): M4 {
  m4Identity(o);
  o[0] = o[5] = o[10] = s;
  return o;
}

/** Поворот Эйлера: сначала X (крен), потом Y (тангаж), потом Z (рыскание), радианы. */
export function m4Euler(o: M4, rx: number, ry: number, rz: number): M4 {
  const cx = Math.cos(rx), sx = Math.sin(rx);
  const cy = Math.cos(ry), sy = Math.sin(ry);
  const cz = Math.cos(rz), sz = Math.sin(rz);
  // R = Rz · Ry · Rx
  o[0] = cz * cy; o[1] = sz * cy; o[2] = -sy; o[3] = 0;
  o[4] = cz * sy * sx - sz * cx; o[5] = sz * sy * sx + cz * cx; o[6] = cy * sx; o[7] = 0;
  o[8] = cz * sy * cx + sz * sx; o[9] = sz * sy * cx - cz * sx; o[10] = cy * cx; o[11] = 0;
  o[12] = 0; o[13] = 0; o[14] = 0; o[15] = 1;
  return o;
}

/** TRS одной матрицей: T(x,y,z) · Euler(rx,ry,rz) · S(s). */
export function m4Trs(o: M4, x: number, y: number, z: number, rx: number, ry: number, rz: number, s = 1): M4 {
  m4Euler(o, rx, ry, rz);
  if (s !== 1) {
    for (let i = 0; i < 3; i++) { o[i] *= s; o[4 + i] *= s; o[8 + i] *= s; }
  }
  o[12] = x; o[13] = y; o[14] = z;
  return o;
}

/** Базис + позиция (столбцы X, Y, Z, T). */
export function m4Basis(o: M4, x: number[], y: number[], z: number[], t: number[]): M4 {
  o[0] = x[0]!; o[1] = x[1]!; o[2] = x[2]!; o[3] = 0;
  o[4] = y[0]!; o[5] = y[1]!; o[6] = y[2]!; o[7] = 0;
  o[8] = z[0]!; o[9] = z[1]!; o[10] = z[2]!; o[11] = 0;
  o[12] = t[0]!; o[13] = t[1]!; o[14] = t[2]!; o[15] = 1;
  return o;
}

/** Точка p (локальная) в мир матрицей m. */
export function m4Point(m: Float32Array, x: number, y: number, z: number): [number, number, number] {
  return [
    m[0]! * x + m[4]! * y + m[8]! * z + m[12]!,
    m[1]! * x + m[5]! * y + m[9]! * z + m[13]!,
    m[2]! * x + m[6]! * y + m[10]! * z + m[14]!,
  ];
}

/** Ортонормировать оси (снять масштаб), сохранив позицию. */
export function m4Orthonormal(o: M4, m: Float32Array): M4 {
  const ax = [m[0]!, m[1]!, m[2]!];
  const ay = [m[4]!, m[5]!, m[6]!];
  norm(ax);
  // Грам-Шмидт: y ⟂ x, z = x × y.
  const d = ax[0]! * ay[0]! + ax[1]! * ay[1]! + ax[2]! * ay[2]!;
  ay[0] -= ax[0]! * d; ay[1] -= ax[1]! * d; ay[2] -= ax[2]! * d;
  norm(ay);
  const az = [ax[1]! * ay[2]! - ax[2]! * ay[1]!, ax[2]! * ay[0]! - ax[0]! * ay[2]!, ax[0]! * ay[1]! - ax[1]! * ay[0]!];
  return m4Basis(o, ax, ay, az, [m[12]!, m[13]!, m[14]!]);
}

export function m4Invert(o: M4, m: Float32Array): M4 {
  // Только для ортонормированных (поворот + перенос).
  const r = _tmp2;
  r[0] = m[0]!; r[1] = m[4]!; r[2] = m[8]!; r[3] = 0;
  r[4] = m[1]!; r[5] = m[5]!; r[6] = m[9]!; r[7] = 0;
  r[8] = m[2]!; r[9] = m[6]!; r[10] = m[10]!; r[11] = 0;
  const tx = m[12]!, ty = m[13]!, tz = m[14]!;
  r[12] = -(r[0] * tx + r[4] * ty + r[8] * tz);
  r[13] = -(r[1] * tx + r[5] * ty + r[9] * tz);
  r[14] = -(r[2] * tx + r[6] * ty + r[10] * tz);
  r[15] = 1;
  o.set(r);
  return o;
}
const _tmp2 = new Float32Array(16);

function norm(v: number[]): void {
  const l = Math.hypot(v[0]!, v[1]!, v[2]!) || 1;
  v[0] /= l; v[1] /= l; v[2] /= l;
}

/** glTF (Y-вверх) → мир (Z-вверх): поворот +90° вокруг X. */
export const GLTF_TO_WORLD: M4 = (() => {
  const m = m4();
  m[5] = 0; m[6] = 1; m[9] = -1; m[10] = 0;
  return m;
})();

export const DEG = Math.PI / 180;
